import React from "react";
import { Box, Button, CircularProgress, Dialog, IconButton, Link, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MarkPaidPayload, SellerPayoutContext, WithdrawalRequest } from "../../../common/requests/transactions/endpoints";
import { C } from "../../../common/theme";
import { formatMoney } from "../../../common/utils/format";
import { Banner, CopyValue, useIsNarrow } from "../../../ui";
import { toDateInput } from "../../Sales/salesData";
import { payoutLineTotals, whatsappUrl } from "./payoutUtils";
import { programmeNote } from "./PayoutOrders";

interface MarkPaidDialogProps {
  request: WithdrawalRequest | null;
  seller?: SellerPayoutContext;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (request: WithdrawalRequest, payload: MarkPaidPayload) => void;
}

/** Records a payout after the bank transfer has gone out. The transfer
 * reference is required so every payout can be traced later. Full screen on
 * phones. */
export default function MarkPaidDialog({ request, seller, busy, error, onCancel, onConfirm }: MarkPaidDialogProps) {
  const narrow = useIsNarrow(600);
  const [reference, setReference] = React.useState("");
  const [paidOn, setPaidOn] = React.useState(toDateInput(new Date()));
  const [note, setNote] = React.useState("");

  React.useEffect(() => {
    if (request) {
      setReference("");
      setPaidOn(toDateInput(new Date()));
      setNote("");
    }
  }, [request]);

  if (!request) return null;
  const today = toDateInput(new Date());
  // Why the amount differs from sales less the usual fee (Bring Your Buyers).
  const programme = request.earnings && !request.currency_mixed ? programmeNote(payoutLineTotals(request.earnings), request.currency) : null;
  const disabled = busy || !reference.trim() || !paidOn || paidOn > today;

  const submit = () => {
    if (disabled) return;
    // A past date is recorded at local noon so it stays on that day in any time zone; today means now.
    const [y, m, d] = paidOn.split("-").map(Number);
    const paidAt = paidOn === today ? new Date() : new Date(y, m - 1, d, 12);
    onConfirm(request, { payout_reference: reference.trim(), paid_at: paidAt.toISOString(), note: note.trim() || undefined });
  };

  return (
    <Dialog
      open
      onClose={busy ? undefined : onCancel}
      fullScreen={narrow}
      maxWidth={false}
      PaperProps={{ sx: { width: narrow ? "100%" : 520, maxWidth: "calc(100vw - 32px)", display: "flex", flexDirection: "column" } }}
    >
      <Box
        component="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          submit();
        }}
        sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            px: { xs: 2, sm: 2.5 },
            pt: { xs: "calc(12px + env(safe-area-inset-top, 0px))", sm: 2 },
            pb: 1.5,
            borderBottom: `1px solid ${C.line}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" component="h2">
              Mark paid
            </Typography>
            <Typography variant="body2" sx={{ color: C.muted, mt: 0.25 }}>
              Record it only after the transfer has gone out.
            </Typography>
          </Box>
          <IconButton onClick={onCancel} disabled={busy} aria-label="Close" sx={{ mr: -1, mt: -0.5, width: 44, height: 44 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, sm: 2.5 }, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography variant="overline" component="div" sx={{ color: C.muted }}>
              Pay
            </Typography>
            <Typography variant="h4" component="div">
              {formatMoney(request.amount, request.currency)}
            </Typography>
            <Typography variant="body2" sx={{ color: C.ink2, overflowWrap: "anywhere" }}>
              to <b>{request.username ?? request.email}</b>
              {request.email && request.username ? ` (${request.email})` : ""}
            </Typography>
            {programme && (
              <Typography variant="body2" sx={{ color: C.muted, mt: 0.5 }}>
                {programme}
              </Typography>
            )}
          </Box>

          {seller?.whatsapp_number ? (
            <Typography variant="body2" component="div" sx={{ color: C.ink2 }}>
              Bank details are collected on WhatsApp:{" "}
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                <CopyValue value={seller.whatsapp_number} mono />
                <Link href={whatsappUrl(seller.whatsapp_number)} target="_blank" rel="noopener noreferrer" underline="hover">
                  Open chat
                </Link>
              </Box>
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: C.muted }}>
              No WhatsApp number on file for this seller; confirm their bank details another way.
            </Typography>
          )}

          {request.currency_mixed && (
            <Banner tone="warning" title="Mixed currencies.">
              This seller's withdrawn earnings are in more than one currency, so this total mixes them. Check the breakdown before paying.
            </Banner>
          )}

          <TextField
            label="Transfer reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            required
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { maxLength: 128 } }}
            helperText="Bank, PayNow or Wise reference, so the payout can be traced"
          />
          <TextField
            label="Paid on"
            type="date"
            value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
          />
          <TextField
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          {error && <Banner tone="danger" title="Not recorded.">{error}</Banner>}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            gap: 1,
            px: { xs: 2, sm: 2.5 },
            pt: 1.5,
            pb: { xs: "calc(12px + env(safe-area-inset-bottom, 0px))", sm: 2 },
            borderTop: `1px solid ${C.line}`,
            "& > *": { flex: { xs: "1 1 auto", sm: "0 0 auto" }, minHeight: 44 },
          }}
        >
          <Button variant="outlined" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disableElevation
            disabled={disabled}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Mark paid
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
